/**
 * Remediation C3 — Firestore-safe notification/partnerRequest queries.
 * Rules require party-field scoping; poNumber-only list queries are rejected.
 */
import {
  collection,
  deleteDoc,
  doc,
  type DocumentData,
  type Firestore,
  getDocs,
  getDoc,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type UpdateData,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_STATUS,
  PARTNER_REQUEST_STATUS,
  DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES,
  isDashboardActionRequiredNotificationType,
} from '../constants/notificationTypes';
import { FirebaseError } from 'firebase/app';
import { APPROVED_CARRIER_PENDING_RATE_CONFIRMATION } from '../constants/rateConfirmationConstants';
import { SHIPPING_SCHEDULE_STATUS } from '../constants/statusValues';
import {
  CHILD_PO_MARKETPLACE_REPOST_BROWSE_SUPPRESSION_TYPES,
  CHILD_PO_MARKETPLACE_REPOST_CANCELLATION_REASON,
  CHILD_PO_PARTNER_ASSIGNMENT_NOTIFICATION_TYPES,
  isPoDocOpenMarketplaceListing,
} from '../constants/childPOMarketplaceRepost';
import {
  batchResolveMultiLoadParentPoNumbers,
  resolveMultiLoadChildParentGateDecision,
  resolveMultiLoadChildParentPoNumber,
} from '../utils/multiLoadMarketplaceParentGate';
import {
  FIRESTORE_NOTIFICATIONS_COLLECTION,
  FIRESTORE_PARTNER_REQUESTS_COLLECTION,
  FIRESTORE_PURCHASE_ORDERS_COLLECTION,
  FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
  FIRESTORE_PO_MARKETPLACE_FLAG,
} from '../constants/firestoreCollectionSecurity';
import Logger from './logger';
const NOTIFICATION_PARTY_FIELDS = [
  'recipientId',
  'senderId',
  'shipperId',
  'brokerId',
  'carrierId',
  'userId',
] as const;

const PARTNER_REQUEST_PARTY_FIELDS = ['carrierId', 'shipperId', 'brokerId', 'userId'] as const;

async function mergePartyFieldQueries(
  db: Firestore,
  collectionName: string,
  partyUid: string,
  partyFields: readonly string[],
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  await Promise.all(
    partyFields.map(async (field) => {
      const snap = await getDocs(
        query(
          collection(db, collectionName),
          where(field, '==', partyUid),
          ...extraConstraints
        )
      );
      snap.docs.forEach((d) => byId.set(d.id, d));
    })
  );
  return [...byId.values()];
}

/** Notifications for a PO where the user is a party (recipient, sender, or role field). */
export async function getNotificationDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(db, FIRESTORE_NOTIFICATIONS_COLLECTION, partyUid, NOTIFICATION_PARTY_FIELDS, [
    where('poNumber', '==', poNumber),
  ]);
}

/**
 * Stale carrier offer notifications superseded on child PO marketplace repost.
 * Party-scoped — R2 Slice 1: notifications reads require a party field equality.
 */
export async function getBrowseSuppressionNotificationDocsForPo(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  await Promise.all(
    CHILD_PO_MARKETPLACE_REPOST_BROWSE_SUPPRESSION_TYPES.map(async (type) => {
      const docs = await mergePartyFieldQueries(
        db,
        FIRESTORE_NOTIFICATIONS_COLLECTION,
        partyUid,
        NOTIFICATION_PARTY_FIELDS,
        [where('poNumber', '==', poNumber), where('type', '==', type)]
      );
      docs.forEach((d) => byId.set(d.id, d));
    })
  );
  return [...byId.values()];
}

/** Clear carrier browse suppressions when a PO is reposted as a fresh marketplace listing. */
export async function clearMarketplaceBrowseSuppressionsForPo(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  options?: { reason?: string }
): Promise<number> {
  const reason = options?.reason ?? CHILD_PO_MARKETPLACE_REPOST_CANCELLATION_REASON;
  const docs = await getBrowseSuppressionNotificationDocsForPo(db, poNumber, partyUid);
  const toClear = docs.filter((docSnap) => {
    const status = String(docSnap.data().status || '').toLowerCase();
    return (
      status === NOTIFICATION_STATUS.REJECTED ||
      status === NOTIFICATION_STATUS.PENDING ||
      status === NOTIFICATION_STATUS.COUNTER_OFFER
    );
  });

  if (toClear.length === 0) return 0;

  const batch = writeBatch(db);
  for (const docSnap of toClear) {
    batch.update(docSnap.ref, {
      status: PARTNER_REQUEST_STATUS.CANCELLED,
      read: true,
      requiresAction: false,
      supersededByMarketplaceRepost: true,
      cancellationReason: reason,
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  await batch.commit();
  return toClear.length;
}

/** Partner requests for a PO where the user is a party. */
export async function getPartnerRequestDocsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  return mergePartyFieldQueries(
    db,
    FIRESTORE_PARTNER_REQUESTS_COLLECTION,
    partyUid,
    PARTNER_REQUEST_PARTY_FIELDS,
    [where('poNumber', '==', poNumber), ...extraConstraints]
  );
}

/**
 * True when the carrier already has a pending partner-assignment notification for this PO.
 * Used to avoid duplicate notifications when healing load + partnerRequest artifacts.
 */
export function isActionablePartnerAssignmentNotificationDoc(
  data: Record<string, unknown>,
  carrierId: string
): boolean {
  const recipient = data.recipientId ?? data.carrierId;
  if (recipient !== carrierId) return false;

  const type = String(data.type || '');
  const partnerTypes = CHILD_PO_PARTNER_ASSIGNMENT_NOTIFICATION_TYPES as readonly string[];
  if (!partnerTypes.includes(type)) return false;

  return String(data.status || '').toLowerCase() === NOTIFICATION_STATUS.PENDING;
}

export async function hasActionablePartnerAssignmentNotification(
  db: Firestore,
  poNumber: string,
  carrierId: string,
  partyUid: string
): Promise<boolean> {
  if (!poNumber || !carrierId) return false;

  const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  for (const uid of [carrierId, partyUid]) {
    if (!uid) continue;
    const docs = await getNotificationDocsForPoAndParty(db, poNumber, uid);
    docs.forEach((d) => byId.set(d.id, d));
  }

  for (const docSnap of byId.values()) {
    if (isActionablePartnerAssignmentNotificationDoc(docSnap.data(), carrierId)) {
      return true;
    }
  }

  return false;
}

/** Delete all notifications for a PO visible to the party user. */
export async function deleteNotificationsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<number> {
  const docs = await getNotificationDocsForPoAndParty(db, poNumber, partyUid);
  const results = await Promise.allSettled(docs.map((d) => deleteDoc(d.ref)));
  const deleted = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    Logger.warn('[deleteNotificationsForPoAndParty] Some deletes failed (continuing)', {
      poNumber,
      failed: failed.length,
      deleted,
    });
  }
  return deleted;
}

/** Delete all partner requests for a PO visible to the party user. */
export async function deletePartnerRequestsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  extraConstraints: QueryConstraint[] = []
): Promise<number> {
  const docs = await getPartnerRequestDocsForPoAndParty(db, poNumber, partyUid, extraConstraints);
  const results = await Promise.allSettled(docs.map((d) => deleteDoc(d.ref)));
  const deleted = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    Logger.warn('[deletePartnerRequestsForPoAndParty] Some deletes failed (continuing)', {
      poNumber,
      failed: failed.length,
      deleted,
    });
  }
  return deleted;
}

/** Batch-update notifications for a PO visible to the party user. */
export async function updateNotificationsForPoAndParty(
  db: Firestore,
  poNumber: string,
  partyUid: string,
  payload: Record<string, unknown>
): Promise<number> {
  const docs = await getNotificationDocsForPoAndParty(db, poNumber, partyUid);
  if (docs.length === 0) return 0;
  const batch = writeBatch(db);
  docs.forEach((d) => batch.update(d.ref, payload as UpdateData<DocumentData>));
  await batch.commit();
  return docs.length;
}

/** Filter party-scoped notification docs by type/status in memory (rules-safe queries). */
export function filterNotificationDocs(
  docs: QueryDocumentSnapshot<DocumentData>[],
  filter: { types?: readonly string[]; status?: string }
): QueryDocumentSnapshot<DocumentData>[] {
  return docs.filter((d) => {
    const data = d.data();
    if (filter.types && !filter.types.includes(data.type)) return false;
    if (filter.status !== undefined && data.status !== filter.status) return false;
    return true;
  });
}

const TERMINAL_PO_STATUSES = new Set([
  'cancelled',
  'canceled', // common misspelling / US spelling in older docs
  'deleted',
  'completed',
  'archived',
  'closed',
]);

function normalizeStatusToken(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
}

function isTerminalStatusToken(value: unknown): boolean {
  const status = normalizeStatusToken(value);
  if (!status) return false;
  if (TERMINAL_PO_STATUSES.has(status)) return true;
  // "completed with tonu" / "completed_without_tonu" style
  if (status.startsWith('completed')) return true;
  if (status.startsWith('cancelled') || status.startsWith('canceled')) return true;
  return false;
}

/** PO still actionable on dashboard (not deleted/cancelled/completed). */
export function isPurchaseOrderActiveForActionRequired(
  poData: Record<string, unknown> | undefined | null
): boolean {
  if (!poData) return false;
  if (poData.archivedAt != null || poData.deletedAt != null || poData.cancelledAt != null) {
    return false;
  }
  if (poData.isArchived === true || poData.isDeleted === true) return false;
  // Check status AND shippingScheduleStatus independently (do not let Processing mask Cancelled schedule).
  if (isTerminalStatusToken(poData.status)) return false;
  if (isTerminalStatusToken(poData.shippingScheduleStatus)) return false;
  if (isTerminalStatusToken(poData.cancellationStatus)) return false;
  const status = normalizeStatusToken(poData.status || poData.shippingScheduleStatus);
  return status.length > 0;
}

const UNKNOWN_LOCATION_LABELS = new Set(['', 'unknown', 'n/a', 'na', '-', '—']);

function firstNonEmptyString(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c != null && typeof c !== 'object' && String(c).trim()) return String(c).trim();
  }
  return '';
}

function cityStateZipFromParts(
  city?: unknown,
  state?: unknown,
  zip?: unknown
): string {
  const parts = [city, state, zip]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean);
  return parts.join(', ');
}

/**
 * Resolve pickup/delivery labels for Action Required cards from PO + notification payloads.
 * Prefer structured city/state over empty address shells so real POs do not look like Unknown.
 */
export function resolveActionRequiredLocations(
  poData: Record<string, unknown> | undefined | null,
  notificationData?: Record<string, unknown> | null
): { pickupLocation: string; deliveryLocation: string } {
  const po = poData || {};
  const vendor = (po.vendorInfo as Record<string, unknown> | undefined) || {};
  const shipTo = (po.shipTo as Record<string, unknown> | undefined) || {};
  const pickupLoc = (po.pickupLocation as Record<string, unknown> | undefined) || {};
  const deliveryLoc = (po.deliveryLocation as Record<string, unknown> | undefined) || {};
  const loadDetails = (notificationData?.loadDetails as Record<string, unknown> | undefined) || {};
  const ldPickup = (loadDetails.pickupLocation as Record<string, unknown> | undefined) || {};
  const ldDelivery = (loadDetails.deliveryLocation as Record<string, unknown> | undefined) || {};

  const pickupLocation =
    firstNonEmptyString(
      vendor.cityStateZip,
      cityStateZipFromParts(vendor.city, vendor.state, vendor.zipCode),
      pickupLoc.address,
      cityStateZipFromParts(pickupLoc.city, pickupLoc.state, pickupLoc.zipCode),
      vendor.streetAddress,
      vendor.address,
      ldPickup.address,
      cityStateZipFromParts(ldPickup.city, ldPickup.state, ldPickup.zipCode),
      po.origin
    ) || 'Unknown';

  const deliveryLocation =
    firstNonEmptyString(
      shipTo.cityStateZip,
      cityStateZipFromParts(shipTo.city, shipTo.state, shipTo.zipCode),
      deliveryLoc.address,
      cityStateZipFromParts(deliveryLoc.city, deliveryLoc.state, deliveryLoc.zipCode),
      shipTo.streetAddress,
      shipTo.address,
      ldDelivery.address,
      cityStateZipFromParts(ldDelivery.city, ldDelivery.state, ldDelivery.zipCode),
      po.destination
    ) || 'Unknown';

  return { pickupLocation, deliveryLocation };
}

/** True when both legs resolve to Unknown/blank — not actionable for reassignment UI. */
export function isActionRequiredRouteUnknown(
  pickupLocation: string,
  deliveryLocation: string
): boolean {
  const pickup = UNKNOWN_LOCATION_LABELS.has(pickupLocation.trim().toLowerCase());
  const delivery = UNKNOWN_LOCATION_LABELS.has(deliveryLocation.trim().toLowerCase());
  return pickup && delivery;
}

const RESOLVED_OFFER_SHIPPING_STATUSES = new Set([
  SHIPPING_SCHEDULE_STATUS.ACTIVE.toLowerCase(),
  SHIPPING_SCHEDULE_STATUS.IN_PROGRESS.toLowerCase(),
]);

/**
 * PO no longer needs shipper action on make_offer / carrier_counter_offer
 * (shipper accepted, carrier signed RC, or load is active on schedule).
 */
export function isPurchaseOrderOfferOrCounterOfferResolved(
  poData: Record<string, unknown> | undefined | null
): boolean {
  if (!poData) return false;

  const shippingScheduleStatus = String(poData.shippingScheduleStatus || '').toLowerCase();
  if (RESOLVED_OFFER_SHIPPING_STATUSES.has(shippingScheduleStatus)) {
    return true;
  }

  if (poData.approvedCarrier != null) return true;
  if (poData[APPROVED_CARRIER_PENDING_RATE_CONFIRMATION] != null) return true;

  return false;
}

/** System-generated load-request copy — not an actionable carrier counter/offer. */
export function isInitialLoadRequestNotificationMessage(message: unknown): boolean {
  if (typeof message !== 'string') return false;
  return message === 'You have a new load request.' || message.includes('new load request');
}

/** Whether a PO is still an open marketplace listing (no assigned carrier). */
export function isPurchaseOrderOpenMarketplaceListingData(
  poData: Record<string, unknown> | undefined | null
): boolean {
  if (!poData || !isPurchaseOrderActiveForActionRequired(poData)) return false;
  if (!isPoDocOpenMarketplaceListing(poData)) return false;
  // Incomplete / abandoned drafts with explicit zero rate must not stay on the board.
  if ('rate' in poData || 'total' in poData) {
    const rate = Number(poData.rate ?? poData.total ?? NaN);
    if (Number.isFinite(rate) && rate <= 0) return false;
  }
  return true;
}

/**
 * carrier_decline Action Required — live rejects must show; only true stale drops.
 *
 * Live (show): PO active, unassigned, carrier rejected (incl. after rate-confirm reject
 * that intentionally returns the PO to open marketplace). Broker dismisses or selects
 * partner/marketplace via dashboard actions — do not auto-ack open marketplace.
 *
 * Stale (hide): terminal/archived PO, assignment already resolved, or carrier already
 * reassigned on the PO.
 */
export function isCarrierDeclineStillActionableForDashboard(
  poData: Record<string, unknown> | undefined | null
): boolean {
  if (!poData || !isPurchaseOrderActiveForActionRequired(poData)) return false;
  if (isPurchaseOrderOfferOrCounterOfferResolved(poData)) return false;
  const carrierId = poData.carrierId;
  if (carrierId != null && String(carrierId).trim() !== '') return false;
  return true;
}

/**
 * Rate-confirmation reject decline (marketplaceApprovalService).
 * These were wrongly soft-acked by the old "open marketplace = stale" gate and must still show
 * until the broker takes a progressing action (then delete).
 */
export function isRateConfirmationCarrierDeclineNotification(
  notificationData: Record<string, unknown> | null | undefined
): boolean {
  if (!notificationData) return false;
  const reason = String(notificationData.rejectionReason || '').toLowerCase();
  const message = String(notificationData.message || '').toLowerCase();
  return reason.includes('agreed rate') || message.includes('declined the agreed rate');
}

/**
 * Dashboard visibility for carrier_decline.
 * - Not actionable PO → delete
 * - Soft-acked (read) non-RC decline → delete (acted/zombie, e.g. 139-01)
 * - Soft-acked RC decline → show if PO still actionable (heal wrong auto-ack)
 * - Unread → show if actionable
 */
export function resolveCarrierDeclineDashboardVisibility(
  notificationData: Record<string, unknown> | null | undefined,
  poData: Record<string, unknown> | null | undefined
): 'show' | 'delete' {
  if (!notificationData) return 'delete';
  if (!isCarrierDeclineStillActionableForDashboard(poData)) return 'delete';
  if (notificationData.read === true) {
    return isRateConfirmationCarrierDeclineNotification(notificationData) ? 'show' : 'delete';
  }
  return 'show';
}

/**
 * make_offer / carrier_counter_offer Action Required — drop corrupt or resolved rows.
 */
export function isOfferOrCounterStillActionableForDashboard(
  poData: Record<string, unknown> | undefined | null,
  notificationData?: Record<string, unknown> | null
): boolean {
  if (!poData || !isPurchaseOrderActiveForActionRequired(poData)) return false;
  if (isPurchaseOrderOfferOrCounterOfferResolved(poData)) return false;

  const notifPo = String(notificationData?.poNumber || '').trim();
  const detailsPo = String(
    (notificationData?.loadDetails as { poNumber?: string } | undefined)?.poNumber || ''
  ).trim();
  if (notifPo && detailsPo && notifPo !== detailsPo) return false;

  if (typeof notificationData?.message === 'string' && notifPo) {
    const mentioned = notificationData.message.match(/PO\s+(PO-[\w-]+)/i);
    if (mentioned?.[1] && mentioned[1] !== notifPo) return false;
  }

  // Marketplace open listing can still receive offers.
  if (isPoDocOpenMarketplaceListing(poData)) return true;

  // Partner path: offer carrier still tied to PO and not resolved.
  const offerCarrier = notificationData?.carrierId;
  if (offerCarrier && poData.carrierId === offerCarrier) return true;

  return false;
}

export type PoOpenListingBatchEntry = {
  /** At least one PO doc was found for this poNumber. */
  found: boolean;
  /** Doc(s) qualify as an open marketplace listing. */
  open: boolean;
};

/** Client fallback when batch-exists API rejects a load that is still listed on the board. */
const PO_OPEN_LISTING_CACHE = new Map<string, { value: PoOpenListingBatchEntry; expiresAt: number }>();
const PO_OPEN_LISTING_CACHE_TTL_MS = 60 * 1000;
const FIRESTORE_IN_QUERY_MAX = 30;

function cachePoOpenListing(poNumber: string, value: PoOpenListingBatchEntry): void {
  PO_OPEN_LISTING_CACHE.set(poNumber, {
    value,
    expiresAt: Date.now() + PO_OPEN_LISTING_CACHE_TTL_MS,
  });
}

/** Clear cached open-listing results after carrier actions (accept/offer) so stale rows do not reappear. */
export function invalidatePoOpenListingCache(poNumbers?: string[]): void {
  if (!poNumbers?.length) {
    PO_OPEN_LISTING_CACHE.clear();
    return;
  }
  for (const raw of poNumbers) {
    const trimmed = raw.trim();
    if (trimmed) PO_OPEN_LISTING_CACHE.delete(trimmed);
  }
}

/** Batched + cached PO open-listing check (avoids N+1 Firestore reads on marketplace browse). */
export async function batchIsPurchaseOrderOpenMarketplaceListing(
  db: Firestore,
  poNumbers: string[]
): Promise<Map<string, PoOpenListingBatchEntry>> {
  const result = new Map<string, PoOpenListingBatchEntry>();
  const now = Date.now();
  const toFetch: string[] = [];

  for (const raw of poNumbers) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const cached = PO_OPEN_LISTING_CACHE.get(trimmed);
    if (cached && cached.expiresAt > now) {
      result.set(trimmed, cached.value);
    } else if (!toFetch.includes(trimmed)) {
      toFetch.push(trimmed);
    }
  }

  if (toFetch.length === 0) return result;

  const poDataByNumber = new Map<string, Record<string, unknown>>();

  for (let i = 0; i < toFetch.length; i += FIRESTORE_IN_QUERY_MAX) {
    const chunk = toFetch.slice(i, i + FIRESTORE_IN_QUERY_MAX);
    chunk.forEach((poNumber) => result.set(poNumber, { found: false, open: false }));

    for (const collectionName of [
      FIRESTORE_PURCHASE_ORDERS_COLLECTION,
      FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    ] as const) {
      // R2 Cutover: poNumber-only list queries are denied under party-scoped PO rules.
      // Prefer marketplace-scoped reads; soft-fail so batch-exists API can authorize open listings.
      try {
        const snap = await getDocs(
          query(
            collection(db, collectionName),
            where('poNumber', 'in', chunk),
            where(FIRESTORE_PO_MARKETPLACE_FLAG, '==', true)
          )
        );
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const pn = String(data.poNumber || '').trim();
          if (!pn || !chunk.includes(pn)) return;
          poDataByNumber.set(pn, data);
          const prev = result.get(pn) || { found: false, open: false };
          prev.found = true;
          if (isPurchaseOrderOpenMarketplaceListingData(data)) {
            prev.open = true;
          }
          result.set(pn, prev);
        });
      } catch (err) {
        Logger.warn(
          `[batchIsPurchaseOrderOpenMarketplaceListing] Client query unavailable for ${collectionName}; deferring to batch-exists API`,
          err instanceof Error ? err : new Error(String(err))
        );
      }
    }

    const parentNumbers = chunk
      .map((pn) => resolveMultiLoadChildParentPoNumber(poDataByNumber.get(pn)))
      .filter((p): p is string => !!p);
    const parentResolution = await batchResolveMultiLoadParentPoNumbers(db, parentNumbers);

    for (const poNumber of chunk) {
      const entry = result.get(poNumber);
      const poData = poDataByNumber.get(poNumber);
      if (!entry?.open || !poData) continue;
      const parentDecision = resolveMultiLoadChildParentGateDecision(poData, parentResolution);
      if (parentDecision === 'allow') {
        continue;
      }
      if (parentDecision === 'deny') {
        // Parent positively terminal / not a shell — hide and do not ask API to reopen.
        entry.open = false;
        continue;
      }
      // Parent unknown under R2 (query denied / not marketplace-readable): defer to batch-exists.
      // found=false lets needsApiCheck consult Admin SDK (which can read parent shells).
      entry.found = false;
      entry.open = false;
    }

    chunk.forEach((poNumber) =>
      cachePoOpenListing(poNumber, result.get(poNumber) || { found: false, open: false })
    );
  }

  return result;
}

/** Client fallback when batch-exists API rejects a load that is still listed on the board. */
export async function isPurchaseOrderOpenMarketplaceListing(
  db: Firestore,
  poNumber: string
): Promise<boolean> {
  const map = await batchIsPurchaseOrderOpenMarketplaceListing(db, [poNumber]);
  return map.get(poNumber.trim())?.open === true;
}

async function deleteNotificationDocIfExists(
  db: Firestore,
  notificationId: string
): Promise<void> {
  if (!notificationId) return;
  try {
    await deleteDoc(doc(db, FIRESTORE_NOTIFICATIONS_COLLECTION, notificationId));
  } catch (err) {
    const code =
      err instanceof FirebaseError
        ? err.code
        : typeof err === 'object' && err && 'code' in err
          ? String((err as { code?: unknown }).code || '')
          : '';
    if (code === 'not-found' || code === 'permission-denied') {
      Logger.debug('[deleteNotificationDocIfExists] Skip missing/denied', {
        notificationId,
        code,
      });
      return;
    }
    throw err;
  }
}

/**
 * Resolve a dashboard Action Required notification after progressing action or stale terminal.
 * Deletes the doc (AR is not retainable). Safe if already gone.
 */
export async function acknowledgeRejectionNotification(
  db: Firestore,
  notificationId: string
): Promise<void> {
  await deleteNotificationDocIfExists(db, notificationId);
}

/**
 * Stale AR → delete. Non-AR tray rows → soft-ack only (read history may still matter).
 */
export async function acknowledgeStaleRejectionNotification(
  db: Firestore,
  notificationId: string
): Promise<void> {
  if (!notificationId) return;
  const snap = await getDoc(doc(db, FIRESTORE_NOTIFICATIONS_COLLECTION, notificationId));
  if (!snap.exists()) return;
  if (isDashboardActionRequiredNotificationType(snap.data().type)) {
    await deleteNotificationDocIfExists(db, notificationId);
    return;
  }
  await updateDoc(snap.ref, {
    read: true,
    requiresAction: false,
    updatedAt: serverTimestamp(),
  });
}

/** Delete all dashboard Action Required notifications for a PO (progressing action / resolve). */
export async function acknowledgeRejectionNotificationsForPo(
  db: Firestore,
  poNumber: string,
  partyUid: string
): Promise<number> {
  const docs = await getNotificationDocsForPoAndParty(db, poNumber, partyUid);
  const toDelete = filterNotificationDocs(docs, {
    types: DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES,
  });
  if (toDelete.length === 0) return 0;
  const batch = writeBatch(db);
  toDelete.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  return toDelete.length;
}

type TrayNotificationLike = {
  id: string;
  read?: boolean;
  type?: string;
  poNumber?: string;
  loadDetails?: { poNumber?: string } | null;
};

/**
 * Clear unread tray notifications whose PO is missing or terminal.
 * Action Required types are deleted; other types are soft-acked (not deleted on read alone).
 * Never clears live carrier rate-confirmation asks — those must stay until the carrier acts.
 */
export async function reconcileStalePoLinkedTrayNotifications(
  db: Firestore,
  notifications: TrayNotificationLike[]
): Promise<number> {
  const candidates = notifications.filter((n) => {
    if (n.read === true) return false;
    // Carrier must still accept/reject rate confirmation after broker/shipper offer accept.
    if (n.type === NOTIFICATION_TYPES.MARKETPLACE_APPROVAL_RATE_CONFIRMATION) return false;
    const po = String(n.poNumber || n.loadDetails?.poNumber || '').trim();
    return Boolean(po && n.id);
  });
  if (candidates.length === 0) return 0;

  const poNumbers = [
    ...new Set(
      candidates.map((n) => String(n.poNumber || n.loadDetails?.poNumber || '').trim()).filter(Boolean)
    ),
  ];

  const activeByPo = new Map<string, boolean>();
  for (let i = 0; i < poNumbers.length; i += FIRESTORE_IN_QUERY_MAX) {
    const chunk = poNumbers.slice(i, i + FIRESTORE_IN_QUERY_MAX);
    const [shipperSnap, brokerSnap] = await Promise.all([
      getDocs(query(collection(db, FIRESTORE_PURCHASE_ORDERS_COLLECTION), where('poNumber', 'in', chunk))),
      getDocs(
        query(collection(db, FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION), where('poNumber', 'in', chunk))
      ),
    ]);
    chunk.forEach((po) => activeByPo.set(po, false));
    const apply = (data: DocumentData) => {
      const pn = String(data.poNumber || '').trim();
      if (!pn) return;
      if (isPurchaseOrderActiveForActionRequired(data as Record<string, unknown>)) {
        activeByPo.set(pn, true);
      }
    };
    shipperSnap.docs.forEach((d) => apply(d.data()));
    brokerSnap.docs.forEach((d) => apply(d.data()));
  }

  let cleared = 0;
  for (const n of candidates) {
    const po = String(n.poNumber || n.loadDetails?.poNumber || '').trim();
    if (activeByPo.get(po)) continue;
    try {
      await acknowledgeStaleRejectionNotification(db, n.id);
      cleared += 1;
    } catch (err) {
      Logger.warn('[reconcileStalePoLinkedTrayNotifications] Failed to clear', {
        notificationId: n.id,
        poNumber: po,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  return cleared;
}
